import { useState, useCallback } from 'react';
import { ApiError } from '../api/client.js';

/**
 * Helper for step up MFA flows. Wraps an async action: if it throws
 * 403 MFA_REQUIRED the modal is shown; once the user verifies, the
 * original action is retried automatically.
 *
 * Usage:
 *   const { run, mfaProps } = useMfaStepUp();
 *   await run(() => usersApi.remove(id));
 *   <MfaPrompt {...mfaProps} />
 */
export function useMfaStepUp() {
  const [open, setOpen] = useState(false);
  const [pendingAction, setPendingAction] = useState(null);

  const run = useCallback(async (action) => {
    try {
      return await action();
    } catch (err) {
      if (err instanceof ApiError && err.code === 'MFA_REQUIRED') {
        return new Promise((resolve, reject) => {
          setPendingAction(() => async () => {
            try {
              const result = await action();
              resolve(result);
            } catch (e) {
              reject(e);
            }
          });
          setOpen(true);
        });
      }
      throw err;
    }
  }, []);

  const onClose = useCallback(() => {
    setOpen(false);
    setPendingAction(null);
  }, []);

  const onVerified = useCallback(async () => {
    setOpen(false);
    if (pendingAction) {
      await pendingAction();
      setPendingAction(null);
    }
  }, [pendingAction]);

  return {
    run,
    mfaProps: { open, onClose, onVerified }
  };
}
